import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const QUICK_REPLIES = [
  "Vorrei un preventivo",
  "Informazioni sui serramenti",
  "Diventa Partner",
  "Assistenza tecnica"
];

const PageLink = ({ to, label }) => (
  <a href={to} className="text-[#7dba00] underline font-bold">{label}</a>
);

const BOT_REPLIES = [
  { match: ["preventivo"], content: <span>Ottima scelta! Puoi richiederci un preventivo gratuito e personalizzato tramite i recapiti nella pagina <PageLink to="/contatti" label="Contatti" />. Il nostro team ti risponderà rapidamente.</span> },
  { match: ["informazioni", "serramenti"], content: <span>Izosmart produce infissi in PVC e Alluminio di altissima qualità, partner ufficiale Schüco. Puoi scoprire tutte le specifiche tecniche nella sezione <PageLink to="/prodotti" label="Prodotti" /> del nostro sito.</span> },
  { match: ["partner"], content: <span>Siamo sempre alla ricerca di professionisti per espandere la nostra rete! Visita la pagina <PageLink to="/lavora-con-noi" label="Lavora con Noi" /> per scoprire i vantaggi della partnership e inviare la tua candidatura.</span> },
  { match: ["assistenza"], content: <span>Il nostro servizio clienti è a tua disposizione. Scrivici a <a href="mailto:info@izosmart.com" className="font-bold underline">info@izosmart.com</a> o chiamaci al <a href="tel:+393509500280" className="font-bold underline">+39 350 950 0280</a>.</span> },
];

const DEFAULT_REPLY = <span>Grazie per il tuo messaggio! Un nostro consulente ti risponderà al più presto. Se hai urgenza, visita la nostra pagina <PageLink to="/contatti" label="Contatti" />.</span>;

const getBotReply = (text) => {
  const lower = text.toLowerCase();
  return BOT_REPLIES.find(r => r.match.some(m => lower.includes(m)))?.content || DEFAULT_REPLY;
};

const QuickReplies = ({ onSelect }) => (
  <div className="mt-4 flex flex-col gap-2 w-full pl-2">
    <p className="text-xs text-gray-500 font-semibold uppercase tracking-widest mb-1">Domande Frequenti:</p>
    <div className="flex flex-wrap gap-2">
      {QUICK_REPLIES.map((qr) => (
        <button
          key={qr}
          data-testid={`chat-quick-reply-${qr.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onSelect(qr)}
          className="bg-white border border-[#7dba00] text-[#7dba00] hover:bg-[#7dba00] hover:text-white text-sm py-1.5 px-3 rounded-full transition-colors text-left"
        >
          {qr}
        </button>
      ))}
    </div>
  </div>
);

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, text: "Ciao! Sono l'assistente virtuale Izosmart. Come posso aiutarti oggi?", sender: "bot" }
  ]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen]);

  const handleSend = (text) => {
    if (!text.trim()) return;
    setMessages(prev => [...prev, { id: Date.now(), text, sender: "user" }]);
    setInput("");
    setTimeout(() => {
      setMessages(prev => [...prev, { id: Date.now() + 1, content: getBotReply(text), sender: "bot" }]);
    }, 1000);
  };

  return (
    <div className="fixed bottom-20 right-4 z-[2147483647] flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-2xl shadow-2xl w-80 sm:w-[400px] mb-4 overflow-hidden border border-gray-200 flex flex-col h-[550px] max-h-[85vh]"
          >
            {/* Header */}
            <div className="bg-[#004259] text-white p-4 flex justify-between items-center shadow-md z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden p-1 shadow-sm">
                  <img src="/media/logo-izosmart.png" alt="Izosmart" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h3 className="font-bold text-sm tracking-wide">Izosmart Assistant</h3>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#7dba00]"></span>
                    <p className="text-xs text-gray-200">Online</p>
                  </div>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 p-2 rounded-full">
                <X size={18} />
              </button>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-[#f8f9fa] flex flex-col gap-4">
              {messages.map((msg, idx) => (
                <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                  <div 
                    className={`max-w-[85%] p-3.5 text-[15px] shadow-sm leading-relaxed ${
                      msg.sender === 'user' 
                        ? 'bg-[#004259] text-white rounded-2xl rounded-tr-sm' 
                        : 'bg-white text-gray-800 border border-gray-100 rounded-2xl rounded-tl-sm'
                    }`}
                  >
                    {msg.content || msg.text}
                  </div>
                  
                  {idx === 0 && messages.length === 1 && <QuickReplies onSelect={handleSend} />}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 bg-white border-t border-gray-100">
              <form onSubmit={(e) => { e.preventDefault(); handleSend(input); }} className="flex gap-2">
                <input 
                  type="text" 
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Scrivi qui..." 
                  className="flex-1 bg-gray-100 border-transparent focus:border-[#7dba00] focus:bg-white focus:ring-1 focus:ring-[#7dba00]/30 rounded-full px-4 py-2.5 text-[15px] outline-none transition-all"
                />
                <button 
                  type="submit"
                  disabled={!input.trim()}
                  className="bg-[#004259] text-white p-2.5 rounded-full hover:bg-[#002f40] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  <Send size={18} className="translate-x-[1px]" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-[60px] h-[60px] rounded-full shadow-[0_8px_30px_rgb(0,0,0,0.3)] flex items-center justify-center text-white transition-transform hover:scale-105 z-[2147483647]"
        style={{ backgroundColor: '#004259' }}
        aria-label="Apri chat"
      >
        {isOpen ? <X size={28} /> : <MessageCircle size={32} />}
      </button>
    </div>
  );
}
